import { useCallback, useEffect, useState } from 'react';
import { useStore } from '../store/useStore';
import { useT } from '../i18n/useT';
import { askConfirm, askNotice, askPrompt } from '../lib/dialogs';
import {
  disableVault,
  enableVault,
  unlockVault,
  vaultStatus,
  type VaultStatus,
} from '../lib/crypto/vault';
import { resyncEverything } from '../lib/sync';

/**
 * Encryption for synced notes.
 *
 * The three things someone needs to know before turning this on are said here
 * rather than in a help page: what the server can still see, that a lost
 * passphrase cannot be recovered, and that the notes on this device stay
 * readable either way.
 */
export function VaultSettings() {
  const t = useT();
  const user = useStore((s) => s.user);
  const setVaultLocked = useStore((s) => s.setVaultLocked);
  const [status, setStatus] = useState<VaultStatus>({ state: 'off' });
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    const next = await vaultStatus(user?.uid ?? null);
    setStatus(next);
    setVaultLocked(next.state === 'locked');
  }, [user?.uid, setVaultLocked]);

  useEffect(() => void refresh(), [refresh]);

  if (!user) {
    return (
      <div className="mb-setting mb-setting-block">
        <label>{t('encrypt.title')}</label>
        <p className="mb-modal-note">{t('encrypt.signedOut')}</p>
      </div>
    );
  }

  const run = async (work: () => Promise<void>) => {
    setBusy(true);
    try {
      await work();
      await refresh();
    } finally {
      setBusy(false);
    }
  };

  const turnOn = () =>
    void run(async () => {
      const ok = await askConfirm({
        title: t('encrypt.confirmTitle'),
        body: t('encrypt.confirmBody'),
        confirmLabel: t('encrypt.turnOn'),
      });
      if (!ok) return;
      const passphrase = await askPrompt({
        title: t('encrypt.choose'),
        confirmLabel: t('common.done'),
      });
      if (!passphrase) return;
      const again = await askPrompt({ title: t('encrypt.repeat'), confirmLabel: t('common.done') });
      if (again !== passphrase) {
        await askNotice({ title: t('encrypt.mismatchTitle'), body: t('encrypt.mismatchBody') });
        return;
      }
      if (!(await enableVault(user.uid, passphrase))) {
        await askNotice({ title: t('encrypt.existsTitle'), body: t('encrypt.existsBody') });
        return;
      }
      // Everything already in the cloud is plaintext: send it all again, sealed.
      await resyncEverything();
    });

  const unlock = () =>
    void run(async () => {
      const passphrase = await askPrompt({
        title: t('encrypt.unlock'),
        confirmLabel: t('encrypt.unlockGo'),
      });
      if (!passphrase) return;
      if (!(await unlockVault(user.uid, passphrase))) {
        await askNotice({ title: t('encrypt.wrongTitle'), body: t('encrypt.wrongBody') });
        return;
      }
      await resyncEverything();
    });

  const turnOff = () =>
    void run(async () => {
      const ok = await askConfirm({
        title: t('encrypt.offTitle'),
        body: t('encrypt.offBody'),
        confirmLabel: t('encrypt.turnOff'),
        danger: true,
      });
      if (!ok) return;
      if (await disableVault(user.uid)) await resyncEverything();
    });

  return (
    <div className="mb-setting mb-setting-block">
      <label>{t('encrypt.title')}</label>
      <div className="mb-backup">
        <p className="mb-modal-note">{t('encrypt.hint')}</p>
        <div className="mb-backup-row">
          <span className="mb-backup-folder">
            {status.state === 'off'
              ? t('encrypt.stateOff')
              : status.state === 'locked'
                ? t('encrypt.stateLocked')
                : t('encrypt.stateOn')}
          </span>
          {status.state === 'off' && (
            <button className="mb-btn" disabled={busy} onClick={turnOn}>
              {t('encrypt.turnOn')}
            </button>
          )}
          {status.state === 'locked' && (
            <button className="mb-btn mb-btn-primary" disabled={busy} onClick={unlock}>
              {t('encrypt.unlockGo')}
            </button>
          )}
          {status.state === 'unlocked' && (
            <button className="mb-btn" disabled={busy} onClick={turnOff}>
              {t('encrypt.turnOff')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
